import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonBase,
  DialogContent,
  SwipeableDrawer,
  CircularProgress,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getLastKnownAuthUser } from "../services/authService";
import { APP_OVERLAY } from "../ui/overlayLayers";

import { getCachedMaterialMasterData, refreshMaterialMasterData } from "../services/materialMasterDataService";
import { getCachedAllocationMasterData, refreshAllocationMasterData } from "../services/allocationMasterDataService";
import { getCachedStorageOwnershipData, refreshStorageOwnershipData } from "../services/storageOwnershipDataService";
import {
  fetchVisionRecognitionServerStatus,
  readCachedActiveVisualLabProfile,
  refreshActiveVisualLabProfileBestEffort,
  visionProductionRuntimeDisplay,
} from "../services/visualLabService";

import FastScrollThumb from "./FastScrollThumb";
import { SystemStatusHarborBackdrop } from "./IndustrialBackdrop";
import SheetScrollbarGuard from "./SheetScrollbarGuard";
import AppDialogSurface from "./AppDialogSurface";
import { useSheetDismiss } from "../hooks/useSheetDismiss";
import { useDeleteArming } from "../hooks/useDeleteArming";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { formatAppIssueDateTime } from "../utils/formatAppDateTime";
import type { AppQueueEntry, AppServiceId, AppServiceStatus, AppStatusIssue, AppVisionOptimizerStatus } from "../status/appStatusTypes";
import { appIssuePalette } from "../status/appIssuePalette";
import { useAppStatus } from "../status/useAppStatus";
import { APP_UI } from "../ui/appUi";
import { recordMasterDataRefreshHistory } from "../services/noticeHistoryService";

type RefreshableDataServiceId = "masterData" | "allocationData" | "ownershipData" | "visionProduction";
const DATA_SERVICE_IDS = new Set<AppServiceId>(["masterData", "allocationData", "ownershipData", "visionProduction"]);

function isRefreshableDataService(id: AppServiceId): id is RefreshableDataServiceId {
  return DATA_SERVICE_IDS.has(id);
}

interface StatusDetailsSheetProps {
  open: boolean;
  issues: AppStatusIssue[];
  services: AppServiceStatus[];
  queueEntries: AppQueueEntry[];
  onClose: () => void;
  onClearIssue: (id: string) => void;
  onClearAll: () => void;
  onOpenHistoryEntry?: (target: { withdrawalId?: number; clientUuid?: string }) => void;
}

type TrafficState = "ok" | "warning" | "error" | "unknown";

function isDataUpdateService(service: AppServiceStatus): boolean {
  if (!DATA_SERVICE_IDS.has(service.id) || service.state !== "warning") return false;
  return service.detail.includes("Neue Version verfügbar") || service.detail.includes("Neuer Stand verfügbar") || service.detail.includes("Aktualisierung");
}

function serviceColor(service: AppServiceStatus): string {
  if (isDataUpdateService(service)) return "#83cee0";
  if (service.state === "ok") return "#43d17a";
  if (service.state === "checking") return "#83cee0";
  if (service.state === "warning") return "#f4a340";
  if (service.state === "error") return "#ff5b62";
  return "#ffffff";
}

function trafficColor(state: TrafficState): string {
  if (state === "ok") return "#43d17a";
  if (state === "warning") return "#f4a340";
  if (state === "error") return "#ff5b62";
  return "#ffffff";
}

function aggregateServices(services: AppServiceStatus[]): TrafficState {
  if (services.some((service) => service.state === "error")) return "error";
  if (services.some((service) => service.state === "warning")) return "warning";
  if (services.some((service) => service.state === "checking")) return "unknown";

  const unknownCount = services.filter((service) => service.state === "unknown").length;
  if (unknownCount === services.length) return "unknown";
  if (unknownCount > 0) return "warning";
  return "ok";
}

function aggregateQueue(entries: AppQueueEntry[]): TrafficState {
  if (entries.some((entry) => entry.state === "failed")) return "error";
  if (entries.some((entry) => entry.state === "pending" || entry.state === "retrying")) return "warning";
  return "ok";
}

function aggregateIssues(issues: AppStatusIssue[]): TrafficState {
  if (issues.some((issue) => issue.severity === "error")) return "error";
  if (issues.some((issue) => issue.severity === "warning")) return "warning";
  return "ok";
}

function queueColor(entry: AppQueueEntry): string {
  if (entry.state === "sent") return "#43d17a";
  if (entry.state === "retrying") return "#83cee0";
  if (entry.state === "pending") return "#f4a340";
  return "#ff5b62";
}

function queueLabel(entry: AppQueueEntry): string {
  if (entry.state === "sent") return "Versendet";
  if (entry.state === "retrying") return "Sendeversuch läuft";
  if (entry.state === "pending") return "Wartet auf Versand";
  return "Fehlgeschlagen";
}

function serviceStateLabel(service: AppServiceStatus): string {
  if (isDataUpdateService(service)) return "Update verfügbar";
  if (service.state === "ok") return "In Ordnung";
  if (service.state === "checking") return "Wird geprüft";
  if (service.state === "warning") return "Warnung";
  if (service.state === "error") return "Fehler";
  return service.detail.toLowerCase().includes("nicht prüfbar") ? "Nicht prüfbar" : "Noch nicht geprüft";
}

function serviceImpact(service: AppServiceStatus): string {
  if (service.state === "checking") {
    return "Der aktuelle Zustand wird gerade ermittelt. Andere Dienste und Bereiche werden unabhängig davon weiter aktualisiert.";
  }
  if (service.state === "unknown") {
    return service.detail.toLowerCase().includes("nicht prüfbar")
      ? "Der Dienst kann aktuell nicht zuverlässig geprüft werden. Das ist kein bestätigter Dienstausfall."
      : "Für diesen Dienst liegt noch kein verlässlicher Prüfstatus vor.";
  }
  if (service.state === "ok") {
    switch (service.id) {
      case "internetConnection": return "Das Gerät kann das öffentliche Internet erreichen.";
      case "backendApi": return "Die Serverfunktionen der App sind erreichbar.";
      case "database": return "Serverdaten können regulär gelesen und gespeichert werden.";
      case "emailWorker": return "Die Versandwarteschlange kann regulär verarbeitet werden.";
      case "smtp": return "Der Mailserver ist für den Versand erreichbar.";
      case "emailRecipient": return "Mindestens ein gültiger Empfänger ist für den Versand hinterlegt.";
      case "fileStorage": return "Erforderliche Dokumente können serverseitig abgelegt werden.";
      case "masterData": return "Materialabgleich und Materialvorschläge stehen mit dem aktuellen Datenstand zur Verfügung.";
      case "allocationData": return "Die Kontierungsauswahl steht mit dem aktuellen Datenstand zur Verfügung.";
      case "ownershipData": return "Die Ordnungspatenschaften stehen mit dem aktuellen Datenstand zur Verfügung.";
      case "visionProduction": return "Die lokal verwendete Etikettenerkennung entspricht dem freigegebenen Stand oder der sicheren Standard-Erkennung.";
      default: return "Der Dienst arbeitet aktuell ohne bekannte Einschränkung.";
    }
  }
  switch (service.id) {
    case "internetConnection": return "Internetabhängige Funktionen und ein externer Serverzugriff können momentan nicht funktionieren. Lokal gespeicherte Daten bleiben verfügbar.";
    case "backendApi": return "Synchronisation, Verlauf, Versand und weitere Serverfunktionen können vorübergehend eingeschränkt oder verzögert sein.";
    case "database": return "Serverdaten können momentan möglicherweise nicht vollständig gelesen oder gespeichert werden. Bereits lokal erfasste Daten bleiben zunächst erhalten.";
    case "emailWorker": return "E-Mails können verzögert verarbeitet werden. Bereits erfasste Entnahmen bleiben gespeichert und Versandaufträge verbleiben in der Warteschlange.";
    case "smtp": return "Der tatsächliche E-Mail-Versand ist momentan nicht möglich oder verzögert.";
    case "emailRecipient": return "Entnahmen können gespeichert werden, der E-Mail-Versand kann ohne gültigen Empfänger jedoch nicht abgeschlossen werden.";
    case "fileStorage": return "Erforderliche Dokumente können möglicherweise nicht vollständig auf dem Server abgelegt werden.";
    case "masterData": return "Materialabgleich und Vorschläge können mit einem älteren oder nur lokal verfügbaren Datenstand arbeiten.";
    case "allocationData": return "Die Kontierungsauswahl kann mit einem älteren oder nur lokal verfügbaren Datenstand arbeiten.";
    case "ownershipData": return "Ordnungspatenschaften können mit einem älteren oder nur lokal verfügbaren Datenstand angezeigt werden.";
    case "visionProduction": return service.detail.includes("Standard-Fallback") ? "Die sichere Standard-Erkennung arbeitet weiter, obwohl der optimierte Stand nicht verwendet werden kann." : "Die aktuelle Erkennung funktioniert weiter; ein neuer administrativ freigegebener Stand kann lokal übernommen werden.";
    default: return "Ein Teil der App-Funktion kann aktuell eingeschränkt sein.";
  }
}

function serviceUserAction(service: AppServiceStatus): string {
  if (service.state === "checking") {
    return "Du musst nichts tun. Die Prüfung läuft automatisch weiter.";
  }
  if (service.state === "unknown") {
    return "Du musst nichts tun. Sobald eine Prüfung wieder möglich ist, wird der Zustand automatisch aktualisiert.";
  }
  if (service.state === "ok") return "Keine Aktion erforderlich.";
  switch (service.id) {
    case "internetConnection": return "Prüfe WLAN oder mobile Daten. Die App testet die Internetverbindung automatisch erneut.";
    case "emailRecipient": return "Wenn der Versand benötigt wird, muss ein Administrator die Empfängerkonfiguration prüfen. Die Entnahme selbst kann weiter erfasst werden.";
    case "masterData": return "Du kannst weiterarbeiten. Prüfe bei wichtigen Materialdaten den angezeigten Listenstand oder warte auf die Aktualisierung.";
    case "allocationData": return "Du kannst weiterarbeiten. Prüfe bei wichtigen Kontierungen den angezeigten Listenstand oder warte auf die Aktualisierung.";
    case "ownershipData": return "Du kannst weiterarbeiten. Prüfe bei wichtigen Ordnungspatenschaften den angezeigten Listenstand oder aktualisiere die Daten.";
    case "visionProduction": return "Du kannst weiterarbeiten. Mit 'Erkennungsstand prüfen' wird ausschließlich der bereits administrativ freigegebene Stand geladen beziehungsweise geprüft; dadurch wird niemals ein Candidate aktiviert.";
    case "smtp":
    case "emailWorker": return "Du kannst weiterarbeiten. Der Versandstatus wird automatisch erneut geprüft und offene Versandaufträge werden nach Möglichkeit erneut verarbeitet.";
    default: return "Du kannst weiterarbeiten, soweit die jeweilige Funktion verfügbar ist. Die App prüft den Zustand automatisch erneut.";
  }
}

function serviceAdminDetails(service: AppServiceStatus): string {
  switch (service.id) {
    case "internetConnection": return "Browser-Netzwerkstatus und externe Erreichbarkeitsprobe prüfen. WLAN/Mobilfunk-Art ist browserabhängig und wird nicht vorausgesetzt.";
    case "backendApi": return "Backend-Container, Netzwerkpfad, Reverse-Proxy/Vite-Proxy, /api/status und Backend-Logs prüfen.";
    case "database": return "PostgreSQL-Container, Datenbankverbindung, Verbindungsparameter und bei Schemafehlern den aktuellen Alembic-Stand prüfen.";
    case "emailWorker": return "Worker-Container, Queue-Zustand, Retry-Planung und Worker-Logs prüfen.";
    case "smtp": return "SMTP-Host, Port, TLS/SSL, Authentifizierung, Zeitüberschreitungen und die konkrete Serverantwort prüfen.";
    case "emailRecipient": return "Empfängerkonfiguration und Gültigkeit der hinterlegten Adressen prüfen. Fehlende oder abgelehnte Empfänger getrennt vom SMTP-Status behandeln.";
    case "fileStorage": return "Ablagepfad, Schreibrechte, Upload-Endpunkt, Dokumenttyp, Zuordnung zur Entnahme und tatsächliche Dateiablage prüfen.";
    case "masterData": return "Serverversion, lokale Revision, Cache, manuelle Overrides und zuletzt freigegebenen Importstand prüfen.";
    case "allocationData": return "Serverversion, lokale Revision, Cache, manuelle Overrides und zuletzt freigegebenen Importstand prüfen.";
    case "ownershipData": return "Serverversion, lokale Revision, Cache, manuelle Overrides und zuletzt freigegebenen Ordnungspatenschafts-Import prüfen.";
    case "visionProduction": return "Lokalen Erkennungsstand, serverseitig aktive Production, Runtime-Kompatibilität und Integritätsstatus vergleichen. Bundle-Import und Candidate bleiben strikt von der manuellen Production-Aktivierung getrennt.";
    default: return "Technische Konfiguration und zugehörige Logs prüfen.";
  }
}

function formatQueueTime(value: string | null): string {
  if (!value) return "Kein weiterer Sendeversuch geplant";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatCheckedAt(value: string | null): string {
  if (!value) return "Noch nicht geprüft";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin",
    dateStyle: "short",
    timeStyle: "medium",
  }).format(date);
}

type ThinStatusIconKind = "pulse" | "services" | "data" | "vision" | "shipping" | "notifications";

function ThinStatusIcon({ kind, size = 22 }: { kind: ThinStatusIconKind; size?: number }) {
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.45,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  return (
    <Box
      component="svg"
      aria-hidden="true"
      viewBox="0 0 24 24"
      sx={{ width: size, height: size, display: "block", overflow: "visible" }}
    >
      {kind === "pulse" && <path d="M2.5 12h4l1.6-5.1 3.2 11.2 2.3-7 1.8 3.4h6.1" {...common} />}
      {kind === "services" && (
        <>
          <circle cx="12" cy="12" r="3.7" {...common} />
          <circle cx="12" cy="12" r="7.1" {...common} />
          <path d="M12 2.3v2.1M12 19.6v2.1M2.3 12h2.1M19.6 12h2.1M5.15 5.15l1.48 1.48M17.37 17.37l1.48 1.48M18.85 5.15l-1.48 1.48M6.63 17.37l-1.48 1.48" {...common} />
        </>
      )}
      {kind === "data" && (
        <>
          <rect x="4" y="4" width="16" height="4" rx="1.4" {...common} />
          <rect x="4" y="10" width="16" height="4" rx="1.4" {...common} />
          <rect x="4" y="16" width="16" height="4" rx="1.4" {...common} />
          <path d="M7 6h.01M7 12h.01M7 18h.01" {...common} />
        </>
      )}
      {kind === "vision" && (
        <>
          <rect x="6.2" y="6.2" width="11.6" height="11.6" rx="2" {...common} />
          <circle cx="12" cy="12" r="2.5" {...common} />
          <path d="M9 2.5v3.2M15 2.5v3.2M9 18.3v3.2M15 18.3v3.2M2.5 9h3.2M2.5 15h3.2M18.3 9h3.2M18.3 15h3.2" {...common} />
        </>
      )}
      {kind === "shipping" && (
        <>
          <path d="M3 6.5h10.5v9H3zM13.5 10h3.6l3.2 3.2v2.3h-6.8z" {...common} />
          <circle cx="7" cy="17.4" r="1.7" {...common} />
          <circle cx="17.2" cy="17.4" r="1.7" {...common} />
        </>
      )}
      {kind === "notifications" && (
        <>
          <path d="M6.5 9.7a5.5 5.5 0 0 1 11 0v3.5l1.7 2.7H4.8l1.7-2.7z" {...common} />
          <path d="M10 18.3a2.2 2.2 0 0 0 4 0M12 3.1V2" {...common} />
        </>
      )}
    </Box>
  );
}

function SectionHeader({
  label,
  subtitle,
  icon,
  state,
  meta,
  checking = false,
  accent = false,
  indicatorColor,
  indicatorGlow = true,
}: {
  label: string;
  subtitle: string;
  icon: ThinStatusIconKind;
  state: TrafficState;
  meta?: string;
  checking?: boolean;
  accent?: boolean;
  indicatorColor?: string;
  indicatorGlow?: boolean;
}) {
  const resolvedIndicatorColor = indicatorColor ?? (accent ? "#83cee0" : trafficColor(state));
  return (
    <Stack direction="row" spacing={1.05} sx={{ alignItems: "center", width: "100%", minWidth: 0 }}>
      {checking ? (
        <CircularProgress size={12} thickness={5} sx={{ color: "#83cee0", flex: "0 0 auto" }} />
      ) : (
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: resolvedIndicatorColor,
            boxShadow: indicatorGlow ? `0 0 10px ${resolvedIndicatorColor}66` : "none",
            flex: "0 0 auto",
          }}
        />
      )}
      <Box sx={{ color: "#58d8f3", width: 24, height: 24, display: "grid", placeItems: "center", flex: "0 0 auto" }}>
        <ThinStatusIcon kind={icon} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ color: "#fff", fontWeight: 820, lineHeight: 1.15 }}>{label}</Typography>
        <Typography variant="caption" sx={{ color: "#9daab1", display: "block", mt: 0.15, lineHeight: 1.2 }}>{subtitle}</Typography>
      </Box>
      {meta && <Typography variant="caption" sx={{ color: "#999ba2", mr: 0.25, flex: "0 0 auto" }}>{meta}</Typography>}
    </Stack>
  );
}

const accordionSx = {
  bgcolor: "rgba(20,22,25,0.94)",
  color: "#fff",
  backgroundImage: "linear-gradient(180deg, rgba(255,255,255,.025), rgba(255,255,255,.008))",
  border: "1px solid rgba(190,199,204,0.17)",
  borderRadius: "10px !important",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,.018), 0 7px 20px rgba(0,0,0,.08)",
  overflow: "hidden",
  "&::before": { display: "none" },
  "& .MuiAccordionSummary-root": { minHeight: 62, px: 1.35 },
  "& .MuiAccordionSummary-content": { my: 0.85 },
  "& .MuiAccordionDetails-root": { px: 1.15, pt: 0, pb: 1.15 },
};

type RecognitionDiagnostics = {
  localLabel: string;
  localBundleId: string | null;
  serverLabel: string;
  serverBundleId: string | null;
  activatedAt: string | null;
  integrityStatus: string | null;
  integrityCheckedAt: string | null;
  integrityReasons: string[];
};

function visionStatusTraffic(snapshot: AppVisionOptimizerStatus | null): TrafficState {
  if (!snapshot) return "unknown";
  if (snapshot.status === "error") return "error";
  if (snapshot.status === "warning") return "warning";
  if (snapshot.status === "ok") return "ok";
  return "unknown";
}

function visionIsDisabled(snapshot: AppVisionOptimizerStatus | null): boolean {
  return Boolean(snapshot && (snapshot.worker.state === "disabled" || snapshot.autopilot.status === "disabled"));
}

function visionIndicatorColor(snapshot: AppVisionOptimizerStatus | null, state: TrafficState): string {
  if (visionIsDisabled(snapshot)) return "#c5c7cc";
  return trafficColor(state);
}

function visionJobLabel(job: AppVisionOptimizerStatus["job"]): string {
  if (!job) return "Kein Heavy-Job aktiv";
  const type = job.type === "dataset_baseline" ? "Production-Baseline" : job.type === "end_to_end_validation" ? "End-to-End-Validation" : job.type === "optuna" ? "Optuna" : job.type === "sweep" ? "Parameter-Sweep" : job.type === "cnn_training" ? "CNN-Training" : job.type === "hybrid_validation" ? "Hybrid-Validation" : job.type === "fast_path_validation" ? "CNN Fast-Path Validation" : job.type === "ocr_optimization" ? "OCR-Optimierung" : job.type === "live_training" ? "LIVE CNN-Training" : job.type === "live_validation" ? "LIVE Real-Validation" : job.type === "ab" ? "A/B-Vergleich" : (job.type || "Optimizer-Job");
  const trialJob = job.type === "optuna" || job.type === "ocr_optimization";
  const semanticCurrent = Number(trialJob ? (job.search_attempt_current ?? job.active_unit ?? job.progress_current ?? 0) : (job.active_unit ?? job.progress_current ?? 0));
  const semanticTotal = Number(trialJob ? (job.search_attempt_total ?? job.progress_total ?? 0) : (job.progress_total ?? 0));
  const progress = semanticTotal > 0 ? ` · ${Math.max(0, semanticCurrent)}/${semanticTotal}${trialJob ? " Trials" : ""}` : "";
  const state = job.status === "blocked" ? " · BLOCKIERT" : job.status === "retry_wait" ? " · Retry wartet" : job.status === "failed" ? " · Fehler" : "";
  return `${type}${progress}${state}`;
}

export default function StatusDetailsSheet({
  open,
  issues,
  services,
  queueEntries,
  onClose,
  onClearIssue,
  onClearAll,
  onOpenHistoryEntry,
}: StatusDetailsSheetProps) {
  const { reportIssue, visionOptimizer: visionSnapshot, refreshSystemStatus } = useAppStatus();
  useEffect(() => {
    if (!open) return;
    // Cached state is rendered immediately. Opening the sheet also requests one
    // background refresh; the regular ~30 s global poll continues unchanged.
    const timer = window.setTimeout(() => { void refreshSystemStatus(); }, 0);
    return () => window.clearTimeout(timer);
  }, [open, refreshSystemStatus]);
  const { armedKey: armedDeleteId, requestDelete, resetDeleteArm } = useDeleteArming<string>();
  const [queueDeleteError, setQueueDeleteError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set());
  const [selectedServiceId, setSelectedServiceId] = useState<AppServiceStatus["id"] | null>(null);
  const [refreshingDataService, setRefreshingDataService] = useState<RefreshableDataServiceId | null>(null);
  const [dataRefreshFeedback, setDataRefreshFeedback] = useState<{ serviceId: RefreshableDataServiceId; kind: "success" | "error"; message: string } | null>(null);
  const selectedService = selectedServiceId ? services.find((service) => service.id === selectedServiceId) ?? null : null;
  const refreshableDataServiceId = selectedService && isRefreshableDataService(selectedService.id) ? selectedService.id : null;
  const isAdmin = getLastKnownAuthUser()?.role === "admin";
  const [recognitionDiagnostics, setRecognitionDiagnostics] = useState<RecognitionDiagnostics | null>(null);
  const activeQueueEntries = queueEntries.filter((entry) => entry.state !== "sent");
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { requestClose } = useSheetDismiss(open, onClose);
  const setSectionExpanded = useCallback((section: string, expanded: boolean) => {
    setExpandedSections((current) => {
      const next = new Set(current);
      if (expanded) next.add(section);
      else next.delete(section);
      return next;
    });
  }, []);
  const closeServiceDetail = () => { setSelectedServiceId(null); setDataRefreshFeedback(null); };
  const { requestClose: requestServiceClose } = useSheetDismiss(
    selectedServiceId !== null,
    closeServiceDetail
  );
  const technicalServices = useMemo(() => services.filter((service) => !DATA_SERVICE_IDS.has(service.id)), [services]);
  const dataServices = useMemo(() => services.filter((service) => DATA_SERVICE_IDS.has(service.id)), [services]);
  const servicesState = useMemo(() => aggregateServices(technicalServices), [technicalServices]);
  const servicesChecking = useMemo(
    () => servicesState === "unknown" && technicalServices.some((service) => service.state === "checking"),
    [technicalServices, servicesState],
  );
  const serviceProblemCount = useMemo(
    () => technicalServices.filter((service) => service.state === "warning" || service.state === "error").length,
    [technicalServices]
  );
  const dataState = useMemo(() => aggregateServices(dataServices), [dataServices]);
  const dataChecking = useMemo(() => dataState === "unknown" && dataServices.some((service) => service.state === "checking"), [dataServices, dataState]);
  const dataProblemCount = useMemo(() => dataServices.filter((service) => service.state === "warning" || service.state === "error").length, [dataServices]);
  const dataOnlyUpdates = useMemo(() => {
    const warnings = dataServices.filter((service) => service.state === "warning");
    return warnings.length > 0
      && !dataServices.some((service) => service.state === "error" || service.state === "checking" || service.state === "unknown")
      && warnings.every(isDataUpdateService);
  }, [dataServices]);
  const queueState = useMemo(() => aggregateQueue(activeQueueEntries), [activeQueueEntries]);
  const issuesState = useMemo(() => aggregateIssues(issues), [issues]);
  const visionState = useMemo(() => visionStatusTraffic(visionSnapshot), [visionSnapshot]);
  const visionDisabled = useMemo(() => visionIsDisabled(visionSnapshot), [visionSnapshot]);
  const visionDotColor = useMemo(() => visionIndicatorColor(visionSnapshot, visionState), [visionSnapshot, visionState]);
  const summaryStates: TrafficState[] = isAdmin
    ? [servicesState, dataState, queueState, issuesState, visionState]
    : [servicesState, dataState, queueState, issuesState];
  const overallState: TrafficState = summaryStates.includes("error")
    ? "error"
    : summaryStates.includes("warning")
      ? "warning"
      : summaryStates.includes("unknown")
        ? "unknown"
        : "ok";
  const systemStatusSubtitle = overallState === "error"
    ? "Mindestens ein System benötigt Aufmerksamkeit."
    : overallState === "warning"
      ? "Einige Systeme sind aktuell eingeschränkt."
      : overallState === "unknown"
        ? "Systemzustände werden gerade geprüft."
        : "Alle Systeme laufen normal.";
  const activeVisionJob = visionSnapshot?.job ?? null;

  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => contentRef.current?.scrollTo({ top: 0, behavior: "auto" }));
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  const loadRecognitionDiagnostics = useCallback(async (): Promise<RecognitionDiagnostics | null> => {
    const cached = readCachedActiveVisualLabProfile();
    const localDisplay = cached ? visionProductionRuntimeDisplay(cached) : null;
    try {
      const server = await fetchVisionRecognitionServerStatus();
      const serverLabel = server.mode === "optimized"
        ? server.recognition_revision ? `Stand ${server.recognition_revision}` : "Optimierter Stand"
        : "Standard-Erkennung";
      return {
        localLabel: localDisplay?.recognitionRevision ? `Stand ${localDisplay.recognitionRevision}` : cached?.production_bundle ? "Optimierter Stand" : "Standard-Erkennung",
        localBundleId: cached?.production_bundle?.bundle_id ?? null,
        serverLabel,
        serverBundleId: server.bundle_id,
        activatedAt: server.activated_at,
        integrityStatus: server.integrity_status,
        integrityCheckedAt: server.integrity_checked_at,
        integrityReasons: server.integrity_reasons ?? [],
      };
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (selectedServiceId !== "visionProduction") return;
    let cancelled = false;
    void loadRecognitionDiagnostics().then((value) => {
      if (!cancelled) setRecognitionDiagnostics(value);
    });
    return () => { cancelled = true; };
  }, [selectedServiceId, loadRecognitionDiagnostics]);

  async function handleDeleteQueueEntry(id: string): Promise<void> {
    if (!requestDelete(id)) {
      setQueueDeleteError(null);
      return;
    }
    resetDeleteArm();
    setQueueDeleteError(null);
    try {
      const response = await fetchWithTimeout(`/api/email/queue/${id}`, {
        method: "DELETE",
        credentials: "include",
      }, 10_000);
      if (!response.ok) {
        let detail = "";
        try { detail = String((await response.json())?.detail ?? ""); } catch { detail = ""; }
        throw new Error(detail || `Queue-Eintrag konnte nicht gelöscht werden (HTTP ${response.status}).`);
      }
      window.dispatchEvent(new Event("materialentnahme:status-refresh"));
    } catch (deleteError) {
      setQueueDeleteError(deleteError instanceof Error ? deleteError.message : "Queue-Eintrag konnte nicht gelöscht werden.");
    }
  }
  async function handleRefreshDataService(serviceId: RefreshableDataServiceId): Promise<void> {
    if (refreshingDataService) return;
    setDataRefreshFeedback(null);
    setRefreshingDataService(serviceId);
    try {
      if (serviceId === "masterData") {
        const before = await getCachedMaterialMasterData();
        const fresh = await refreshMaterialMasterData();
        const previousVersion = before?.version.versionNumber ?? null;
        const currentVersion = fresh.version.versionNumber;
        const manualRevisionChanged = (fresh.version.manualRevision ?? null) !== (before?.version.manualRevision ?? null);
        reportIssue({ id: `master-version-${currentVersion ?? "manual"}`, severity: "info", title: "Materialstammliste aktualisiert", detail: currentVersion !== previousVersion ? `Version ${previousVersion ?? "–"} → ${currentVersion ?? "–"} wurde übernommen.` : manualRevisionChanged ? "Die verfügbaren manuellen Änderungen wurden übernommen." : "Die Materialstammliste wurde erneut geladen." });
        void recordMasterDataRefreshHistory({ kind: "material", versionNumber: currentVersion, previousVersionNumber: previousVersion, manualRevisionChanged });
        window.dispatchEvent(new Event("materialentnahme:master-data-status-updated"));
      } else if (serviceId === "allocationData") {
        const before = await getCachedAllocationMasterData();
        const fresh = await refreshAllocationMasterData();
        const previousVersion = before?.version.versionNumber ?? null;
        const currentVersion = fresh.version.versionNumber;
        const manualRevisionChanged = (fresh.version.manualRevision ?? null) !== (before?.version.manualRevision ?? null);
        reportIssue({ id: "allocation-manual-refresh", severity: "info", title: "Kontierungsliste aktualisiert", detail: currentVersion !== previousVersion ? `Version ${previousVersion ?? "–"} → ${currentVersion ?? "–"} wurde übernommen.` : manualRevisionChanged ? "Die verfügbaren manuellen Änderungen wurden übernommen." : "Die Kontierungsliste wurde erneut geladen." });
        void recordMasterDataRefreshHistory({ kind: "allocation", versionNumber: currentVersion, previousVersionNumber: previousVersion, manualRevisionChanged });
        window.dispatchEvent(new Event("materialentnahme:allocation-data-status-updated"));
      } else if (serviceId === "ownershipData") {
        const before = await getCachedStorageOwnershipData();
        const fresh = await refreshStorageOwnershipData();
        const previousVersion = before?.version.versionNumber ?? null;
        const currentVersion = fresh.version.versionNumber;
        const manualRevisionChanged = (fresh.version.manualRevision ?? null) !== (before?.version.manualRevision ?? null);
        reportIssue({ id: "ownership-manual-refresh", severity: "info", title: "Ordnungspatenschaften aktualisiert", detail: currentVersion !== previousVersion ? `Version ${previousVersion ?? "–"} → ${currentVersion ?? "–"} wurde übernommen.` : manualRevisionChanged ? "Die verfügbaren manuellen Änderungen wurden übernommen." : "Die Ordnungspatenschaften wurden erneut geladen." });
        void recordMasterDataRefreshHistory({ kind: "ownership", versionNumber: currentVersion, previousVersionNumber: previousVersion, manualRevisionChanged });
        window.dispatchEvent(new Event("materialentnahme:ownership-data-status-updated"));
      } else {
        const refreshed = await refreshActiveVisualLabProfileBestEffort();
        if (!refreshed) throw new Error("Erkennungsstand konnte aktuell nicht vom Server geprüft werden.");
        setRecognitionDiagnostics(await loadRecognitionDiagnostics());
      }
      setDataRefreshFeedback({ serviceId, kind: "success", message: serviceId === "masterData" ? "Materialstammliste erfolgreich aktualisiert." : serviceId === "allocationData" ? "Kontierungsliste erfolgreich aktualisiert." : serviceId === "ownershipData" ? "Ordnungspatenschaften erfolgreich aktualisiert." : "Erkennungsstand erfolgreich geprüft und übernommen." });
      window.dispatchEvent(new Event("materialentnahme:status-refresh"));
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : serviceId === "masterData" ? "Materialstammliste konnte nicht aktualisiert werden." : serviceId === "allocationData" ? "Kontierungsliste konnte nicht aktualisiert werden." : serviceId === "ownershipData" ? "Ordnungspatenschaften konnten nicht aktualisiert werden." : "Erkennungsstand konnte nicht geprüft werden.";
      setDataRefreshFeedback({ serviceId, kind: "error", message });
      window.dispatchEvent(new Event("materialentnahme:status-refresh"));
    } finally {
      setRefreshingDataService(null);
    }
  }



  return (
    <>
      <SwipeableDrawer
        anchor="top"
        open={open}
        onOpen={() => undefined}
        onClose={requestClose}
        disableSwipeToOpen
        disableDiscovery
        disableBackdropTransition
        hysteresis={0.20}
        minFlingVelocity={220}
        transitionDuration={{ enter: 200, exit: 180 }}
        ModalProps={{ disableRestoreFocus: true, keepMounted: true }}
        slotProps={{
          transition: {
            // Der Drawer bleibt absichtlich gemountet. Nach jedem abgeschlossenen
            // Close-Zyklus wird der Accordion-Zustand zurückgesetzt, sodass der
            // nächste Open-Zyklus garantiert vollständig eingeklappt startet.
            onExited: () => setExpandedSections(new Set()),
          },
          paper: {
            sx: {
              mt: 0,
              mx: "auto",
              width: "100%",
              maxWidth: 600,
              height: "min(92dvh, 760px)",
              maxHeight: "92dvh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              willChange: "transform",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              position: "relative",
              borderRadius: "0 0 24px 24px",
              color: "#f7f7f8",
              backgroundColor: "#101215",
              backgroundImage: "none",
              border: "1px solid rgba(190,201,207,0.13)",
              borderTopColor: "rgba(91,207,231,0.24)",
              boxShadow: "0 22px 56px rgba(0,0,0,.74), inset 0 1px 0 rgba(255,255,255,.025)",
            },
          },
        }}
        sx={{
          zIndex: APP_OVERLAY.systemStatus,
          "& .MuiBackdrop-root": { backgroundColor: "rgba(10,12,14,0.74)", zIndex: -1 },
        }}
      >
        <SystemStatusHarborBackdrop />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
        <DialogContent
          ref={contentRef}
          sx={{
            p: 0,
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
          }}
        >
          <Stack spacing={1} sx={{ px: 1.5, pt: "calc(3.55rem + env(safe-area-inset-top))", pb: 1.5 }}>
            <Stack direction="row" spacing={1.2} sx={{ alignItems: "center", px: 0.65, pb: 0.5, position:"relative" }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ color: "#58d8f3", mb: 0.55 }}><ThinStatusIcon kind="pulse" size={25} /></Box>
                <Typography variant="h6" sx={{ color: "#fff", fontWeight: 850, lineHeight: 1.15 }}>Systemstatus</Typography>
                <Typography variant="caption" sx={{ color: "#a8a9ae", display: "block", mt: 0.35 }}>{systemStatusSubtitle}</Typography>
              </Box>
              <IconButton
                aria-label="Systemstatus schließen"
                onClick={requestClose}
                sx={{
                  color: "#fff",
                  width: 42,
                  height: 42,
                  border: "1px solid rgba(255,255,255,.17)",
                  bgcolor: "rgba(24,27,31,.975)",
                }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </Stack>

            <Accordion disableGutters sx={accordionSx} expanded={expandedSections.has("services")} onChange={(_, expanded) => setSectionExpanded("services", expanded)}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: "#aeb0b6" }} />}>
                <SectionHeader
                  label="Dienste"
                  subtitle={servicesChecking ? "Dienste werden geprüft" : servicesState === "ok" ? "Alle Dienste sind aktiv" : serviceProblemCount > 0 ? `${serviceProblemCount} Dienst${serviceProblemCount === 1 ? "" : "e"} mit Hinweis` : "Status nicht vollständig bekannt"}
                  icon="services"
                  state={servicesState}
                  checking={servicesChecking}
                />
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={0.55}>
                  {technicalServices.map((service) => (
                      <ButtonBase
                        key={service.id}
                        component="div"
                        onClick={() => { setDataRefreshFeedback(null); setSelectedServiceId(service.id); }}
                        sx={{
                          display: "flex",
                          width: "100%",
                          textAlign: "left",
                          alignItems: "center",
                          gap: 1,
                          px: 0.8,
                          py: 0.75,
                          borderRadius: 2.7,
                          bgcolor: "rgba(24,27,31,.98)",
                          opacity: 1,
                        }}
                      >
                        {service.state === "checking" ? <CircularProgress size={14} thickness={5} sx={{ color: "#83cee0", flex: "0 0 auto" }} /> : <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: serviceColor(service), flex: "0 0 auto" }} />}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ color: "#fff", fontWeight: 760 }}>{service.label}</Typography>
                          <Typography variant="caption" sx={{ color: isDataUpdateService(service) ? "#83cee0" : "#a8a9ae", display: "block", fontWeight: isDataUpdateService(service) ? 800 : 400 }}>{service.detail}</Typography>
                        </Box>
                        <InfoOutlinedIcon sx={{ color: serviceColor(service), fontSize: 19, flex: "0 0 auto" }} />
                      </ButtonBase>
                    ))}
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters sx={accordionSx} expanded={expandedSections.has("master-data")} onChange={(_, expanded) => setSectionExpanded("master-data", expanded)}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: "#aeb0b6" }} />}>
                <SectionHeader
                  label="Daten & Erkennung"
                  subtitle={dataChecking ? "Datenstände werden geprüft" : dataOnlyUpdates ? "Aktualisierung verfügbar" : dataState === "ok" ? "Daten und Erkennung aktuell" : dataProblemCount > 0 ? `${dataProblemCount} Bereich${dataProblemCount === 1 ? "" : "e"} mit Hinweis` : "Status nicht vollständig bekannt"}
                  icon="data"
                  state={dataState}
                  checking={dataChecking}
                  accent={dataOnlyUpdates}
                />
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={0.55}>
                  {dataServices.map((service) => (
                    <ButtonBase
                      key={service.id}
                      component="div"
                      onClick={() => { setDataRefreshFeedback(null); setSelectedServiceId(service.id); }}
                      sx={{ display:"flex", width:"100%", textAlign:"left", alignItems:"center", gap:1, px:.8, py:.75, borderRadius:2.7, bgcolor:"rgba(24,27,31,.98)" }}
                    >
                      {service.state === "checking" ? <CircularProgress size={14} thickness={5} sx={{ color:"#83cee0", flex:"0 0 auto" }}/> : <Box sx={{ width:9, height:9, borderRadius:"50%", bgcolor:serviceColor(service), flex:"0 0 auto" }}/>}
                      <Box sx={{ minWidth:0, flex:1 }}>
                        <Typography variant="body2" sx={{ color:"#fff", fontWeight:760 }}>{service.label}</Typography>
                        <Typography variant="caption" sx={{ color:isDataUpdateService(service) ? "#83cee0" : "#a8a9ae", display:"block", fontWeight:isDataUpdateService(service) ? 800 : 400 }}>{service.detail}</Typography>
                      </Box>
                      <InfoOutlinedIcon sx={{ color:serviceColor(service), fontSize:19, flex:"0 0 auto" }}/>
                    </ButtonBase>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>

            {isAdmin && (
              <Accordion disableGutters sx={accordionSx} expanded={expandedSections.has("vision-optimizer")} onChange={(_, expanded) => setSectionExpanded("vision-optimizer", expanded)}>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: "#aeb0b6" }} />}>
                  <SectionHeader
                    label="Vision Optimizer"
                    subtitle={visionDisabled ? "Bewusst deaktiviert" : activeVisionJob ? "Autopilot & Lernjob aktiv" : visionSnapshot?.message ?? "Autopilot & Lernstatus"}
                    icon="vision"
                    state={visionState}
                    checking={false}
                    indicatorColor={visionDotColor}
                    indicatorGlow={!visionDisabled}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={0.55}>
                    {!visionSnapshot ? (
                      <Typography variant="body2" sx={{ color: "#a8a9ae", px: .8, py: .6 }}>Noch kein globaler Optimizer-Status empfangen. Beim Öffnen wurde bereits eine Aktualisierung im Hintergrund angestoßen.</Typography>
                    ) : (
                      <>
                        <Box sx={{ px:.8, py:.7, borderRadius:2.7, bgcolor:"rgba(24,27,31,.98)" }}>
                          <Stack direction="row" spacing={.7} sx={{ alignItems:"center" }}><Box sx={{ width:9, height:9, borderRadius:"50%", bgcolor:visionDotColor,boxShadow:visionDisabled?"none":undefined }}/><Box sx={{ minWidth:0, flex:1 }}><Typography variant="body2" sx={{ color:"#fff", fontWeight:800 }}>Autopilot</Typography><Typography variant="caption" sx={{ color:visionState === "error" ? "#ff8b8f" : visionState === "warning" ? "#f4a340" : "#a8a9ae", display:"block" }}>{activeVisionJob?.status === "blocked" ? (activeVisionJob.blocked_reason || activeVisionJob.message || visionSnapshot.message) : activeVisionJob ? (activeVisionJob.message || visionSnapshot.message) : (visionSnapshot.autopilot.last_message || visionSnapshot.message)}</Typography></Box></Stack>
                        </Box>
                        <Box sx={{ px:.8, py:.7, borderRadius:2.7, bgcolor:"rgba(24,27,31,.98)" }}>
                          <Typography variant="body2" sx={{ color:"#fff", fontWeight:800 }}>Aktueller Heavy-Job</Typography>
                          <Typography variant="caption" sx={{ color: activeVisionJob ? "#83cee0" : "#a8a9ae", display:"block" }}>{visionJobLabel(activeVisionJob)}</Typography>
                        </Box>
                        <Box sx={{ display:"grid", gridTemplateColumns:"repeat(2,minmax(0,1fr))", gap:.55 }}>
                          <Box sx={{ px:.8, py:.7, borderRadius:2.7, bgcolor:"rgba(24,27,31,.98)" }}><Typography variant="caption" sx={{ color:"#a8a9ae" }}>Worker</Typography><Typography variant="body2" sx={{ color:"#fff", fontWeight:800 }}>{visionDisabled?"Deaktiviert":visionSnapshot.worker.state}</Typography><Typography variant="caption" sx={{ color:"#a8a9ae", display:"block" }}>{visionSnapshot.worker.age_seconds == null ? "Heartbeat unbekannt" : `Heartbeat vor ${Math.round(visionSnapshot.worker.age_seconds)} s`}</Typography></Box>
                          <Box sx={{ px:.8, py:.7, borderRadius:2.7, bgcolor:"rgba(24,27,31,.98)" }}><Typography variant="caption" sx={{ color:"#a8a9ae" }}>Circuit Breaker</Typography><Typography variant="body2" sx={{ color:visionSnapshot.circuit.state === "open" ? "#ff8b8f" : "#fff", fontWeight:800 }}>{visionSnapshot.circuit.state}</Typography><Typography variant="caption" sx={{ color:"#a8a9ae", display:"block" }}>{visionSnapshot.circuit.consecutive_failures} technische Fehler im Fenster</Typography></Box>
                          {visionSnapshot.decision?.target_url && <Button size="small" variant="outlined" onClick={() => { onClose(); window.location.assign(visionSnapshot.decision!.target_url!); }} sx={{ gridColumn:"1 / -1", minHeight:32, color:visionSnapshot.decision.severity === "error" ? APP_UI.error : APP_UI.warning, borderColor:visionSnapshot.decision.severity === "error" ? "rgba(255,91,98,.45)" : "rgba(244,163,64,.45)", borderRadius:999, textTransform:"none", fontWeight:850 }}>Passende VO-Details öffnen</Button>}
                        </Box>
                        <Typography variant="caption" sx={{ color:"#a8a9ae", px:.2 }}>Wird zusammen mit allen anderen Diensten über den globalen Systemstatus aktualisiert.</Typography>
                      </>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            <Accordion disableGutters sx={accordionSx} expanded={expandedSections.has("shipping-history")} onChange={(_, expanded) => setSectionExpanded("shipping-history", expanded)}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: "#aeb0b6" }} />}>
                <SectionHeader
                  label="Versand & Verlauf"
                  subtitle={activeQueueEntries.length === 0 ? "Keine Verzögerungen" : `${activeQueueEntries.length} Vorgang${activeQueueEntries.length === 1 ? "" : "e"} offen`}
                  icon="shipping"
                  state={queueState}
                />
              </AccordionSummary>
              <AccordionDetails>
                {queueDeleteError && (
                  <Box sx={{ mb: .6, px: .9, py: .7, borderRadius: 2.4, border: "1px solid rgba(255,91,98,.34)", bgcolor: "rgba(36,23,25,.98)" }}>
                    <Typography variant="body2" sx={{ color: "#ff7a7f", fontWeight: 800 }}>{queueDeleteError}</Typography>
                  </Box>
                )}
                {activeQueueEntries.length === 0 ? (
                  <Stack direction="row" spacing={0.8} sx={{ alignItems: "center", px: 0.8, py: 0.8 }}>
                    <CheckCircleOutlineRoundedIcon sx={{ color: "#43d17a", fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: "#b7b8bd" }}>Keine fehlgeschlagenen oder ausstehenden Einträge.</Typography>
                  </Stack>
                ) : (
                  <Stack spacing={0.6}>
                    {activeQueueEntries.map((entry) => {
                      const armed = armedDeleteId === entry.id;
                      return (
                        <Box key={entry.id} sx={{ display: "flex", gap: 0.5, alignItems: "stretch", px: 0.35, py: 0.35, borderRadius: 2.7, bgcolor: "rgba(24,27,31,.98)" }}>
                          <ButtonBase
                            disabled={(!entry.withdrawalId && !entry.clientUuid) || !onOpenHistoryEntry}
                            onClick={() => { if (entry.withdrawalId || entry.clientUuid) onOpenHistoryEntry?.({ withdrawalId: entry.withdrawalId ?? undefined, clientUuid: entry.clientUuid ?? undefined }); }}
                            sx={{ flex: 1, minWidth: 0, gap: .9, alignItems: "flex-start", justifyContent: "flex-start", textAlign: "left", px: .45, py: .45, borderRadius: 2.3 }}
                          >
                          {entry.state === "retrying" || entry.state === "pending"
                            ? <HourglassBottomRoundedIcon sx={{ color: queueColor(entry), fontSize: 20, mt: 0.15, flex: "0 0 auto" }} />
                            : <ErrorOutlineRoundedIcon sx={{ color: queueColor(entry), fontSize: 20, mt: 0.15, flex: "0 0 auto" }} />}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ color: "#fff", fontWeight: 760 }}>{entry.title}</Typography>
                            {entry.diagnosticTest && <Chip label="Diagnose-Test" size="small" sx={{ mt:.35, height:20, color:"#b78cff", border:"1px solid rgba(183,140,255,.38)", bgcolor:"rgba(183,140,255,.09)", fontWeight:950, fontSize:"0.64rem" }} />}
                            {entry.withdrawalDate && <Typography variant="caption" sx={{ color: "#a8a9ae", display: "block" }}>Entnahmedatum: {new Date(`${entry.withdrawalDate}T00:00:00`).toLocaleDateString("de-DE", { timeZone: "Europe/Berlin" })}</Typography>}
                            <Typography variant="caption" sx={{ color: queueColor(entry), display: "block" }}>
                              {queueLabel(entry)}
                              {entry.attempts > 0
                                ? ` · ${entry.kind === "sync" ? "Synchronisationsversuch" : "Sendeversuch"} ${entry.attempts} von ${entry.maxAttempts}`
                                : ` · max. ${entry.maxAttempts} Versuche`}
                            </Typography>
                            {entry.nextAttemptAt && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#a8a9ae",
                                  display: "block",
                                  whiteSpace: "nowrap",
                                  fontSize: "0.72rem",
                                }}
                              >
                                Nächster {entry.kind === "sync" ? "Synchronisationsversuch" : "Sendeversuch"}: {formatQueueTime(entry.nextAttemptAt)}
                              </Typography>
                            )}
                            {entry.state === "retrying" && !entry.nextAttemptAt && (
                              <Typography variant="caption" sx={{ color: "#a8a9ae", display: "block" }}>
                                {entry.kind === "sync" ? "Recovery wird beim nächsten Queue-Lauf geprüft" : "Sendeversuch wird gerade ausgeführt"}
                              </Typography>
                            )}
                            {entry.state === "failed" && !entry.nextAttemptAt && (
                              <Typography variant="caption" sx={{ color: "#a8a9ae", display: "block" }}>
                                {entry.kind === "sync" ? "Automatische Synchronisierung beendet · manuell erneut versuchen" : "Kein weiterer Sendeversuch geplant"}
                              </Typography>
                            )}
                            {entry.detail && <Typography variant="caption" sx={{ color: "#a8a9ae", display: "block" }}>{entry.detail}</Typography>}
                            {(entry.withdrawalId || entry.clientUuid) && <Typography variant="caption" sx={{ color: "#83cee0", display: "block", mt: .2, fontWeight: 800 }}>Im Verlauf öffnen ›</Typography>}
                          </Box>
                          </ButtonBase>
                          {!entry.id.startsWith("sync:") && (
                            <Button
                              aria-label={armed ? "Löschen bestätigen" : "Eintrag löschen"}
                              onClick={() => void handleDeleteQueueEntry(entry.id.replace(/^email:/, ""))}
                              startIcon={armed ? undefined : <DeleteOutlineRoundedIcon fontSize="small" />}
                              sx={{
                                minWidth: armed ? 78 : 40,
                                width: armed ? "auto" : 40,
                                height: 40,
                                px: armed ? 1.05 : 0,
                                borderRadius: 999,
                                color: armed ? "#8fe5a0" : "#ff7a7f",
                                bgcolor: armed ? "rgba(105,210,125,.07)" : "transparent",
                                border: armed ? "1px solid rgba(105,210,125,.35)" : "1px solid transparent",
                                boxShadow: "none",
                                transition: "all 180ms ease",
                                textTransform: "none",
                                fontWeight: 850,
                                "& .MuiButton-startIcon": { m: armed ? undefined : 0 },
                                "&:hover": { bgcolor: armed ? "#e03842" : "rgba(255,91,98,0.08)" },
                              }}
                            >
                              {armed ? "✓" : ""}
                            </Button>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters sx={accordionSx} expanded={expandedSections.has("notifications")} onChange={(_, expanded) => setSectionExpanded("notifications", expanded)}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: "#aeb0b6" }} />}>
                <SectionHeader label="Meldungen" subtitle={issues.length === 0 ? "Keine neuen Meldungen" : `${issues.length} Meldung${issues.length === 1 ? "" : "en"} offen`} icon="notifications" state={issuesState} />
              </AccordionSummary>
              <AccordionDetails>
                {issues.length === 0 ? (
                  <Stack direction="row" spacing={0.8} sx={{ alignItems: "center", px: 0.8, py: 0.8 }}>
                    <CheckCircleOutlineRoundedIcon sx={{ color: "#43d17a", fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: "#b7b8bd" }}>Keine offenen Meldungen.</Typography>
                  </Stack>
                ) : (
                  <Stack spacing={0.6}>
                    {issues.map((issue) => {
                      const palette = appIssuePalette(issue);
                      return (
                        <Box key={issue.id} sx={{ display: "flex", gap: 0.9, alignItems: "flex-start", px: 0.8, py: 0.8, borderRadius: 2.7, bgcolor: palette.background ?? "rgba(24,27,31,.98)", border: `1px solid ${palette.border}` }}>
                          {issue.severity === "error"
                            ? <ErrorOutlineRoundedIcon sx={{ color: palette.accent, fontSize: 20 }} />
                            : issue.severity === "warning"
                              ? <WarningAmberRoundedIcon sx={{ color: palette.accent, fontSize: 20 }} />
                              : <CheckCircleOutlineRoundedIcon sx={{ color: palette.accent, fontSize: 20 }} />}
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" spacing={0.65} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.45 }}>
                              {issue.adminOnly && <Chip label="ADMIN" size="small" sx={{ height: 20, color: palette.chipText, border: `1px solid ${palette.chipBorder}`, bgcolor: palette.chipBackground, fontWeight: 950, fontSize: "0.64rem", letterSpacing: ".055em" }} />}
                              {issue.diagnosticTest && <Chip label="Diagnose-Test" size="small" sx={{ height: 20, color: "#b78cff", border: "1px solid rgba(183,140,255,.38)", bgcolor: "rgba(183,140,255,.09)", fontWeight: 950, fontSize: "0.64rem" }} />}
                              <Typography variant="body2" sx={{ color: "#fff", fontWeight: 800 }}>{issue.title}</Typography>
                            </Stack>
                            {issue.detail && <Typography variant="caption" sx={{ color: "#a8a9ae", display: "block" }}>{issue.detail}</Typography>}
                            <Typography variant="caption" sx={{ color: "#777b82", display: "block", mt: .18 }}>{formatAppIssueDateTime(issue.createdAt)}</Typography>
                            {issue.actionLabel && issue.actionEvent && <Button size="small" variant="outlined" onClick={() => { onClose(); window.dispatchEvent(new Event(issue.actionEvent!)); }} sx={{ mt: .6, minHeight: 32, color: palette.accent, borderColor: palette.border, borderRadius: 999, textTransform: "none", fontWeight: 800 }}>{issue.actionLabel}</Button>}
                            {issue.actionLabel && issue.actionUrl && <Button size="small" variant="outlined" onClick={() => { onClose(); window.location.assign(issue.actionUrl!); }} sx={{ mt: .6, minHeight: 32, color: palette.accent, borderColor: palette.border, borderRadius: 999, textTransform: "none", fontWeight: 800 }}>{issue.actionLabel}</Button>}
                          </Box>
                          <Button onClick={() => onClearIssue(issue.id)} size="small" sx={{ minWidth: 0, px: 1, color: palette.accent, borderRadius: 999, fontWeight: 750 }}>OK</Button>
                        </Box>
                      );
                    })}
                    {issues.length > 1 && (
                      <Button onClick={onClearAll} variant="outlined" sx={{ alignSelf: "center", minHeight: 40, px: 2, borderRadius: 999, borderColor: "rgba(131,206,224,0.45)", color: "#83cee0", fontWeight: 800 }}>
                        Alle Meldungen quittieren
                      </Button>
                    )}
                  </Stack>
                )}
              </AccordionDetails>
            </Accordion>
          </Stack>
        </DialogContent>

        <SheetScrollbarGuard>
          <FastScrollThumb
            targetRef={contentRef}
            contained
            minScrollablePx={1}
            zIndex={APP_OVERLAY.systemStatus + 40}
            right={1}
          />
        </SheetScrollbarGuard>
        </Box>

        <Box
          data-system-status-swipe-handle
          aria-hidden="true"
          sx={{
            flex: "0 0 56px",
            minHeight: 56,
            position: "relative",
            zIndex: 3,
            display: "grid",
            placeItems: "center",
            touchAction: "pan-y",
            cursor: "grab",
            userSelect: "none",
            WebkitUserSelect: "none",
            bgcolor: "rgba(16,18,21,.96)",
            borderTop: "1px solid rgba(255,255,255,0.065)",
            boxShadow: "0 -8px 18px rgba(0,0,0,.16)",
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 4,
              borderRadius: 99,
              bgcolor: "rgba(224,231,235,0.38)",
              boxShadow: "0 1px 0 rgba(0,0,0,.50)",
            }}
          />
        </Box>
      </SwipeableDrawer>

      <AppDialogSurface
        open={selectedService !== null}
        onClose={requestServiceClose}
        title={selectedService?.label ?? "Dienstinformation"}
        subtitle={selectedService ? serviceStateLabel(selectedService) : undefined}
        zIndex={APP_OVERLAY.systemStatusChild}
      >
        {selectedService && (
          <>
            <Box sx={{ p: 1.25, borderRadius: 2.7, bgcolor: "rgba(24,27,31,.98)" }}>
              <Typography variant="caption" sx={{ color: "#92949a", display: "block", mb: 0.25 }}>Status</Typography>
              <Typography variant="body2" sx={{ color: "#f0f0f2", lineHeight: 1.5 }}>{selectedService.detail}</Typography>
            </Box>
            {selectedService.id === "visionProduction" && recognitionDiagnostics && (
              <Box sx={{ p: 1.05, borderRadius: 2.5, bgcolor: "rgba(23,31,34,.98)", border: "1px solid rgba(131,206,224,.18)" }}>
                <Typography variant="caption" sx={{ color:"#83cee0", fontWeight:850, display:"block", mb:.35 }}>Erkennungsstand</Typography>
                <Typography variant="body2" sx={{ color:"#d9dadd" }}>Lokal: {recognitionDiagnostics.localLabel}</Typography>
                <Typography variant="body2" sx={{ color:"#d9dadd" }}>Server aktiv: {recognitionDiagnostics.serverLabel}</Typography>
                <Typography variant="caption" sx={{ color:"#92949a", display:"block", mt:.25 }}>Aktiv seit: {formatCheckedAt(recognitionDiagnostics.activatedAt)}</Typography>
                {isAdmin && (
                  <>
                    <Typography variant="caption" sx={{ color:"#92949a", display:"block" }}>Integrität: {recognitionDiagnostics.integrityStatus ?? "Noch nicht geprüft"} · zuletzt {formatCheckedAt(recognitionDiagnostics.integrityCheckedAt)}</Typography>
                    <Typography variant="caption" sx={{ color:"#73757c", display:"block", overflowWrap:"anywhere" }}>Lokal Bundle-ID: {recognitionDiagnostics.localBundleId ?? "–"} · Server Bundle-ID: {recognitionDiagnostics.serverBundleId ?? "–"}</Typography>
                    {recognitionDiagnostics.integrityReasons.length > 0 && <Typography variant="caption" sx={{ color:"#f4a340", display:"block" }}>{recognitionDiagnostics.integrityReasons.join(" · ")}</Typography>}
                  </>
                )}
              </Box>
            )}
            <Box>
              <Typography variant="caption" sx={{ color: "#92949a", display: "block", mb: 0.25 }}>Auswirkung</Typography>
              <Typography variant="body2" sx={{ color: "#c4c5c9", lineHeight: 1.55 }}>{serviceImpact(selectedService)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: "#92949a", display: "block", mb: 0.25 }}>Was kann ich tun?</Typography>
              <Typography variant="body2" sx={{ color: "#c4c5c9", lineHeight: 1.55 }}>{serviceUserAction(selectedService)}</Typography>
            </Box>
            {isAdmin && (
              <Box sx={{ p: 1.1, borderRadius: 2.5, border: "1px solid rgba(131,206,224,.22)", bgcolor: "rgba(23,31,34,.98)" }}>
                <Typography variant="caption" sx={{ color: "#83cee0", display: "block", mb: 0.25, fontWeight: 850 }}>Technische Details · Administrator</Typography>
                <Typography variant="body2" sx={{ color: "#c4c5c9", lineHeight: 1.55 }}>{serviceAdminDetails(selectedService)}</Typography>
              </Box>
            )}
            {refreshableDataServiceId && dataRefreshFeedback?.serviceId === refreshableDataServiceId && (
              <Box sx={{ p: 1.05, borderRadius: 2.4, border: dataRefreshFeedback.kind === "success" ? "1px solid rgba(67,209,122,.34)" : "1px solid rgba(255,91,98,.34)", bgcolor: dataRefreshFeedback.kind === "success" ? "rgba(23,33,27,.98)" : "rgba(36,23,25,.98)" }}>
                <Typography variant="body2" sx={{ color: dataRefreshFeedback.kind === "success" ? "#69d27d" : "#ff7a7f", fontWeight: 800 }}>
                  {dataRefreshFeedback.message}
                </Typography>
              </Box>
            )}
            {refreshableDataServiceId && !(dataRefreshFeedback?.serviceId === refreshableDataServiceId && dataRefreshFeedback.kind === "success") && (
              <Button
                variant="outlined"
                disabled={refreshingDataService !== null}
                onClick={() => void handleRefreshDataService(refreshableDataServiceId)}
                startIcon={refreshingDataService === refreshableDataServiceId ? <CircularProgress size={16} thickness={5} /> : undefined}
                sx={{ borderRadius: 999, borderColor: "rgba(131,206,224,.40)", color: "#83cee0", textTransform: "none", fontWeight: 850 }}
              >
                {refreshingDataService === refreshableDataServiceId ? "Wird aktualisiert …" : refreshableDataServiceId === "masterData" ? "Materialstammliste aktualisieren" : refreshableDataServiceId === "allocationData" ? "Kontierungen aktualisieren" : refreshableDataServiceId === "ownershipData" ? "Ordnungspatenschaften aktualisieren" : "Erkennungsstand prüfen"}
              </Button>
            )}
            <Typography variant="caption" sx={{ color: "#7f8188" }}>Zuletzt geprüft: {formatCheckedAt(selectedService.updatedAt)}</Typography>
            <Button variant="contained" onClick={requestServiceClose} sx={{ alignSelf: "flex-end", borderRadius: 999, bgcolor: "#83cee0", color: "#0a3139", fontWeight: 850, boxShadow: "none" }}>Verstanden</Button>
          </>
        )}
      </AppDialogSurface>
    </>
  );
}
